import { Calendar, Download, Package, RotateCcw, Truck } from "lucide-react"
import { format } from "date-fns"

import { OrderStatus, type Order } from "@/api/api-client"
import { FulfillmentDetails } from "@/components/orders/FulfillmentDetails"
import { OrderFinancialSummary } from "@/components/orders/OrderFinancialSummary"
import { OrderPipeline } from "@/components/orders/OrderPipeline"
import { OrderStatusBadge } from "@/components/orders/OrderStatusBadge"
import { getOrderStatusPresentation } from "@/components/orders/order-status-presentation"
import { Button } from "@/components/ui/button"

import { BuyerShipmentAccordion } from "./BuyerShipmentAccordion"
import type { BuyerOrderCardLabels } from "./buyer-order-types"
import {
  BUYER_ORDER_PIPELINE_STEPS,
  canReorderOrder,
  getBuyerOrderItemsCount,
  getBuyerOrderTotalAmount,
  getDeliveredAt,
  getShippingQuoteSubOrders,
} from "./buyer-order-utils"
import { PaymentRequiredStrip } from "./PaymentRequiredStrip"

interface BuyerOrderCardProps {
  order: Order
  labels: BuyerOrderCardLabels
  commonLabels: {
    quantity: string
    total: string
  }
  isCheckingOut: boolean
  checkingOutSubOrderId: number | null
  awaitingPaymentSubOrderIds?: ReadonlySet<number>
  expandedPickupIds: Set<number>
  onPayNow: (subOrderId: number) => void
  onDownloadInvoice: () => void
  onTrackShipment: () => void
  onReorder: (order: Order) => void
  onTogglePickupAddress: (subOrderId: number) => void
}

export function BuyerOrderCard({
  order,
  labels,
  commonLabels,
  isCheckingOut,
  checkingOutSubOrderId,
  awaitingPaymentSubOrderIds,
  expandedPickupIds,
  onPayNow,
  onDownloadInvoice,
  onTrackShipment,
  onReorder,
  onTogglePickupAddress,
}: BuyerOrderCardProps) {
  const currentStatus = order.status as OrderStatus
  const workflow = getOrderStatusPresentation(currentStatus, labels.statuses)
  const totalItemsCount = getBuyerOrderItemsCount(order)
  const totalAmount = getBuyerOrderTotalAmount(order)
  const deliveredAt = getDeliveredAt(order)
  const canReorder = canReorderOrder(order)

  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-background shadow-sm">
      <div className="grid grid-cols-1 divide-y divide-border lg:grid-cols-4 lg:divide-x lg:divide-y-0">
        <div className="space-y-6 p-6">
          <div className="flex items-baseline justify-between">
            <OrderStatusBadge status={currentStatus} labels={labels.statuses} />
            <span className="flex items-center gap-1.5 text-lg font-bold text-foreground">
              <Calendar className="h-3.5 w-3.5" />{" "}
              {order.orderDate && format(new Date(order.orderDate), "MMM dd, yyyy")}
            </span>
          </div>
          <div>
            <h2 className="font-display text-4xl tracking-tighter text-foreground">{order.id}</h2>
            <p className="mt-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">
              {labels.orderId}
            </p>
          </div>
          <div className="flex items-center gap-4 pt-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5" />{" "}
              {labels.itemsCount.replace("{count}", totalItemsCount.toString())}
            </span>
          </div>
        </div>

        <OrderPipeline
          title={labels.pipelineProgress || "LOGISTICS PROGRESS"}
          steps={BUYER_ORDER_PIPELINE_STEPS(labels.statuses)}
          currentStepIndex={workflow.stepIndex}
        />

        <OrderFinancialSummary
          title={labels.orderTotal}
          totalAmount={totalAmount}
          sections={getShippingQuoteSubOrders(order.subOrders).map((subOrder) => {
            const shippingCost = subOrder.shippingCost ?? 0
            const productsCost = (subOrder.totalAmount ?? 0) - shippingCost
            return {
              key: subOrder.id ?? `${subOrder.masterOrderId}-shipping-quote`,
              title: `${labels.reference} ${subOrder.id}`,
              rows: [
                { label: labels.productSubtotal, amount: productsCost },
                ...(shippingCost > 0 ? [{ label: labels.shippingCost, amount: shippingCost }] : []),
                { label: labels.total, amount: subOrder.totalAmount ?? 0, highlight: true },
              ],
            }
          })}
        />

        <FulfillmentDetails
          title={labels.shippingTitle || "SHIPPING & DELIVERY"}
          recipientName={order.receiverName}
          phone={order.phone}
          street={order.street}
          externalNumber={order.externalNumber}
          internalNumber={order.internalNumber}
          city={order.city}
          state={order.state}
          deliveredAt={deliveredAt}
          deliveredLabel={
            deliveredAt
              ? labels.deliveredAt.replace("{date}", format(deliveredAt, "dd MMM yyyy"))
              : undefined
          }
          showEstimatedDelivery={order.status !== OrderStatus.OrderDeclined}
          estimatedDeliveryLabel={labels.estimatedDelivery}
        />
      </div>

      <PaymentRequiredStrip
        subOrders={order.subOrders}
        labels={labels}
        isCheckingOut={isCheckingOut}
        checkingOutSubOrderId={checkingOutSubOrderId}
        awaitingPaymentSubOrderIds={awaitingPaymentSubOrderIds}
        onPayNow={onPayNow}
      />

      <div className="flex flex-col gap-3 border-t border-border bg-muted/40 px-6 py-3 sm:flex-row sm:items-center sm:justify-end">
        {order.status === OrderStatus.OrderComplete && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDownloadInvoice}
            className="h-8 gap-1.5 border border-border text-xs font-bold uppercase tracking-widest"
          >
            <Download className="h-3.5 w-3.5" />
            {labels.downloadInvoice}
          </Button>
        )}

        {order.status === OrderStatus.OrderShipped && (
          <Button
            type="button"
            size="sm"
            onClick={onTrackShipment}
            className="h-8 gap-1.5 text-xs font-bold uppercase tracking-widest"
          >
            <Truck className="h-3.5 w-3.5" />
            {labels.trackShipment}
          </Button>
        )}

        {canReorder && (
          <Button
            type="button"
            size="sm"
            onClick={() => onReorder(order)}
            className="h-8 gap-1.5 text-xs font-bold uppercase tracking-widest"
            style={{
              backgroundColor: "oklch(var(--action))",
              color: "oklch(var(--action-foreground))",
            }}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {labels.reorder}
          </Button>
        )}
      </div>

      <BuyerShipmentAccordion
        order={order}
        labels={labels}
        commonLabels={commonLabels}
        statusLabels={labels.statuses}
        expandedPickupIds={expandedPickupIds}
        onTogglePickupAddress={onTogglePickupAddress}
      />
    </div>
  )
}
